import type { FlagKey, UserRole } from '@doneverse/shared'
import { useLocation, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { getHomeRoute, matchesPathPrefix } from '@/lib/routes'

// `roles`, when set, narrows the flag to those roles; every other role is let through
// (their own role guard and the API still decide whether they may be on the page).
const FLAGGED_ROUTE_PREFIXES = [
  { flag: 'groups', prefix: '/groups' },
  { flag: 'workflows', prefix: '/workflows' },
  { flag: 'doers-university', prefix: '/doers-university' },
  { flag: 'masterclass-vault', prefix: '/masterclass-vault' },
  { flag: 'founders-lounge', prefix: '/founders-lounge' },
  { flag: 'tech-stack-finder', prefix: '/tech-stack-finder' },
  // Cross-doer title search (ClickUp 86d4cqpjz). Per-user, and deliberately NOT in
  // ADMIN_UNGATED_FLAGS. The API is admin-only and enforces the same flag; this only
  // redirects.
  { flag: 'activity-search', prefix: '/admin/activity-search' },
  // Activity Summary exposes fleet-wide hours and productivity to an admin. Per-user,
  // and deliberately NOT in ADMIN_UNGATED_FLAGS. Its own flag since ClickUp 14zf25zn1pp,
  // separate from the CSV export flag. The API enforces the same flag for admins; this
  // only redirects. A `dsl` reads a team-scoped copy with no flag (the API scopes it), so
  // the flag gates admins only and a DSL is never bounced from here.
  { flag: 'activity-summary', prefix: '/admin/activity-summary', roles: ['admin'] },
  // The two surfaces that exist only for the onboarding flow, gated by the
  // rollout flag that draws the tracker rather than one of their own. A second
  // flag over a step's surface can only say "you are in the rollout, and the
  // step you were sent to is closed" — the tracker then has to withhold the
  // CTA, which is a step no founder can finish. The flag-off contract is that
  // they keep the old experience entirely, so both go with the tracker.
  //
  // Step 2's is additionally not passive: mounting it opens an ElevenLabs
  // session whose first act creates an onboarding session row, so a founder
  // outside the rollout could grow onboarding state by typing the URL.
  { flag: 'founder-onboarding', prefix: '/founder-intake' },
  { flag: 'founder-onboarding', prefix: '/foundations' },
  // Step 10's Launch Day (ClickUp 86d40tuc8). Same reasoning, and unlike the
  // launch checklist beside it in the sidebar this one CAN be flagged: it has no
  // doer audience, so gating it bounces nobody who has business being there.
  // Outside the rollout it could only ever say "you have no launch day", because
  // a founder with no onboarding run has no step 10 either.
  { flag: 'founder-onboarding', prefix: '/launch-day' },
  // Doer coaching rollout — prep/chat, submitted work and session history ship
  // together behind the existing coaching flag.
  { flag: 'pre-session-prep', prefix: '/pre-session-prep' },
  { flag: 'pre-session-prep', prefix: '/ai-coaching' },
  { flag: 'pre-session-prep', prefix: '/submit-work' },
  { flag: 'pre-session-prep', prefix: '/coaching-sessions' },
  // Loyalty is deliberately NOT on the coaching flag. Closing a session is what
  // writes to the ledger, so it shipped bundled with coaching — but the display
  // is Chunk 1 only (no redemption, tiers or leaderboard) and the coach quotes a
  // different number than the ledger records (ClickUp 86d3g821r). Its own flag
  // lets coaching launch without it. Accrual is server-side and unaffected.
  { flag: 'loyalty-points', prefix: '/loyalty' },
  // Task container rollout. The ordinary task board/detail/create routes stay
  // available; only the routes whose UI is itself a Project or Collection are
  // withheld. APIs and existing memberships remain untouched.
  { flag: 'task-containers', prefix: '/tasks/project' },
  { flag: 'task-containers', prefix: '/tasks/collections' },
  { flag: 'task-containers', prefix: '/tasks/projects' },
  // A doer's own activity (ClickUp 86d40rj6v). The API enforces the same flag;
  // this only redirects. Not in ADMIN_UNGATED_FLAGS: admins never use this page.
  { flag: 'my-activity', prefix: '/settings/my-activity' },
] as const satisfies readonly {
  flag: FlagKey
  prefix: string
  roles?: readonly UserRole[]
}[]

/**
 * Rollout flags that never gate an admin. Both name a surface an admin owns
 * rather than consumes: the unified groups view is their only groups surface,
 * and the masterclass lesson page is where the CMS editor's "View live" sends
 * the author of the course (ClickUp 86d46fzdu). A flag governing the founder
 * rollout must not hide either from them.
 *
 * This only opens the routes their role guard already admits — `/groups` for
 * the unified view, `/masterclass-vault/$courseSlug` for the preview. The
 * masterclass catalog beside it stays founder-only on its own guard.
 */
const ADMIN_UNGATED_FLAGS: readonly FlagKey[] = ['groups', 'masterclass-vault']

export function getRequiredFlag(pathname: string, role?: UserRole): FlagKey | undefined {
  const entry = FLAGGED_ROUTE_PREFIXES.find(({ prefix }) => matchesPathPrefix(pathname, prefix))
  if (!entry) return undefined
  // Without a role the entry's role scope can't apply, so the flag is required as before.
  const scope = (entry as { roles?: readonly UserRole[] }).roles
  if (role && scope && !scope.includes(role)) return undefined
  return entry.flag
}

export function useFeatureFlagRouteGuard() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { loading, profile } = useAuth()
  const { flags } = useFeatureFlags()

  useEffect(() => {
    if (loading || !profile) return

    const requiredFlag = getRequiredFlag(pathname, profile.role)
    if (!requiredFlag || flags.includes(requiredFlag)) return

    if (profile.role === 'admin' && ADMIN_UNGATED_FLAGS.includes(requiredFlag)) return

    const homeRoute = getHomeRoute(profile.role)
    if (pathname === homeRoute) return

    void navigate({ to: homeRoute, replace: true })
  }, [flags, loading, navigate, pathname, profile])
}
